<template>
  <div>

    <async-panel :status="initStatus">

      <!-- editable version -->

      <v-form v-if="canWriteLocations" @submit.prevent="submit" novalidate>

        <o-switch
          v-for="(_value, solution) in formState"
          :key="solution"
          :label="$t(`enums.solutions.${solution}`)"
          v-model="formState[solution]"
        />

        <template v-if="formState.uri && !formState.reporting">
          <v-alert type="warning" class="mb-4">
            URI will not work unless you also enable Reporting
          </v-alert>
        </template>

        <v-btn
          :text="$t('actions.save')"
          type="submit"
          prepend-icon="$confirm"
          :loading="submitting"
          :disabled="submitting"
        />

      </v-form>

      <!-- read-only version -->

      <template v-if="!canWriteLocations">
        
        <o-text-field
          v-for="(_value, solution) in formState"
          :key="solution"
          :label="$t(`enums.solutions.${solution}`)"
          :model-value="formState[solution] ? t('misc.yes') : t('misc.no')"
          readonly
        />

      </template>

    </async-panel>

  </div>
</template>

<script setup lang="ts">

import { inject, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import AsyncPanel from '../../AsyncPanel.vue'

// models
import Location from '../../../models/Location'
import LocationSolution from '../../../models/LocationSolution'

// misc
import asyncStatus from '../../../utils/asyncStatus'
import NotificationService from '../../../services/NotificationService'
import {useTitle} from '@vueuse/core'
import type { Can } from '../../../main'

const props = defineProps({

  location: {
    type: Location,
    required: true,
  },

})

const { t } = useI18n()
const $can = inject('$can') as Can
const canWriteLocations = $can('locations.write')

const title = useTitle()
title.value = t('titles.location_solutions', {location_name: props.location.locationName})

/**
 * Form state
 */

const formState = reactive({
  universalInventory: false,
  rateManagement: false,
  blackouts: false,
  reporting: false,
  uri: false,
})

/**
 * Initialization 
 */

const initStatus = asyncStatus()

initialize()

function initialize() {

  initStatus.setBusy()

  LocationSolution.fetchByLocationID(props.location.ID)
    .then(() => {
      const locationSolution = LocationSolution.query().where('locationID', props.location.ID).first()
      if (locationSolution) {
        formState.universalInventory = locationSolution.universalInventory
        formState.rateManagement = locationSolution.rateManagement
        formState.blackouts = locationSolution.blackouts
        formState.reporting = locationSolution.reporting
        formState.uri = locationSolution.uri
      }
      initStatus.setReady()
    })
    .catch((error) => {
      initStatus.setError(error)
    })

}

/**
 * Submission
 */

const submitting = ref<boolean>(false)

async function submit() {

  if (!canWriteLocations) {
    return
  }

  submitting.value = true

  const payload = {
    locationID: props.location.ID,
    universalInventory: formState.universalInventory,
    rateManagement: formState.rateManagement,
    blackouts: formState.blackouts,
    reporting: formState.reporting,
    // the FE app doesn't deal with these permission, but the BE expects them
    rateParity: false,
    maxCapacity: false,
    uri: formState.uri,
  }

  LocationSolution.saveExisting(payload)
    .then(() => {
      NotificationService.success(t('alerts.solutions_updated'))
    })
    .catch((error) => {
      console.error(error)
      const msg = t('alerts.couldnt_update_solutions')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
